<script lang="ts">
    import { errorState, hideErrorOverlay, reloadPage } from '@/lib/errorHandler.svelte';
    import { Button } from '@/components/ui/button';
    import { AlertTriangle, RefreshCw, X, Send, Check, Loader2, Server } from 'lucide-svelte';
    import { page } from '@inertiajs/svelte';
    import axios from 'axios';

    let sendingReport = $state(false);
    let reportSent = $state(false);
    let sendError = $state<string | null>(null);

    // Get support email from Inertia props
    const supportEmail = $derived($page.props.supportEmail as string | null);

    // Determine if this is a server error
    const isServerError = $derived(errorState.type === 'server');

    // Build error details for display and reporting
    const errorDetails = $derived(() => {
        let details = errorState.message || 'Unknown error';

        if (isServerError && errorState.httpContext) {
            const ctx = errorState.httpContext;
            details = `${ctx.method || 'REQUEST'} ${ctx.url || 'unknown'}\n\nStatus: ${ctx.status || 'unknown'}\n\nMessage: ${errorState.message || 'Unknown error'}`;
        }

        return details;
    });

    async function sendErrorReport() {
        if (sendingReport || reportSent) return;

        sendingReport = true;
        sendError = null;

        try {
            // Build comprehensive error message including HTTP context for server errors
            let fullMessage = errorState.message || 'Unknown error';
            if (isServerError && errorState.httpContext) {
                const ctx = errorState.httpContext;
                fullMessage = `[Server Error] ${ctx.method || 'REQUEST'} ${ctx.url || 'unknown'} - Status ${ctx.status || 'unknown'}: ${errorState.message || 'Unknown error'}`;
            }

            await axios.post('/web-api/error-report', {
                error_message: fullMessage,
                page_url: window.location.href,
                error_stack: errorState.stack,
            });
            reportSent = true;
        } catch (err) {
            sendError = 'Failed to send report. Please try again.';
            console.error('[Error Report] Failed to send:', err);
        } finally {
            sendingReport = false;
        }
    }

    // Reset state when error overlay is hidden
    $effect(() => {
        if (!errorState.visible) {
            reportSent = false;
            sendError = null;
        }
    });
</script>

{#if errorState.visible}
    <div
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="error-title"
        aria-describedby="error-description"
    >
        <div class="mx-4 w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
            <div class="flex flex-col items-center text-center">
                <div class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                    {#if isServerError}
                        <Server class="h-6 w-6 text-destructive" />
                    {:else}
                        <AlertTriangle class="h-6 w-6 text-destructive" />
                    {/if}
                </div>

                <h2 id="error-title" class="mb-2 text-lg font-semibold">
                    {#if isServerError}
                        Server Error
                    {:else}
                        Something went wrong
                    {/if}
                </h2>

                <p id="error-description" class="mb-6 text-sm text-muted-foreground">
                    {#if isServerError}
                        The server encountered an error processing your request. You can try again or reload the page.
                    {:else}
                        An unexpected error occurred. You can try reloading the page or continue using the app.
                    {/if}
                </p>

                {#if errorState.message || (isServerError && errorState.httpContext)}
                    <details class="mb-6 w-full text-left">
                        <summary class="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            Show error details
                        </summary>
                        <pre class="mt-2 max-h-32 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap">{errorDetails()}</pre>
                    </details>
                {/if}

                <div class="flex w-full flex-col gap-2 sm:flex-row">
                    <Button
                        variant="default"
                        class="flex-1"
                        onclick={reloadPage}
                    >
                        <RefreshCw class="mr-2 h-4 w-4" />
                        Reload Page
                    </Button>

                    <Button
                        variant="outline"
                        class="flex-1"
                        onclick={hideErrorOverlay}
                    >
                        <X class="mr-2 h-4 w-4" />
                        Try to Continue
                    </Button>
                </div>

                {#if supportEmail}
                    <div class="mt-4 w-full border-t pt-4">
                        {#if reportSent}
                            <p class="flex items-center justify-center gap-2 text-sm text-green-600">
                                <Check class="h-4 w-4" />
                                Error report sent successfully
                            </p>
                        {:else}
                            <Button
                                variant="ghost"
                                class="w-full text-muted-foreground hover:text-foreground"
                                onclick={sendErrorReport}
                                disabled={sendingReport}
                            >
                                {#if sendingReport}
                                    <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                {:else}
                                    <Send class="mr-2 h-4 w-4" />
                                    Send Error Report
                                {/if}
                            </Button>
                            {#if sendError}
                                <p class="mt-2 text-xs text-destructive">{sendError}</p>
                            {/if}
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    </div>
{/if}